<template>
  <article class="card paper-card paper-card--stamped" :style="{ '--delay': `${delay}s` }">
    <!-- Corner position chip — numbered like a baseball card -->
    <div class="card__corner">
      <span class="card__n numeric">N° {{ paddedIndex }}</span>
      <span class="card__position">{{ player.position ?? '—' }}</span>
    </div>

    <header class="card__header">
      <h3 class="card__name">{{ player.name }}</h3>
      <span class="card__team numeric">{{ player.team.toUpperCase() }}</span>
    </header>

    <hr class="card__rule" />

    <!-- Expected line -->
    <dl class="card__stats">
      <div class="stat">
        <dt class="stat__label">Exp. H</dt>
        <dd class="stat__value numeric">{{ fmt(meanHits) }}</dd>
      </div>
      <div class="stat">
        <dt class="stat__label">Exp. HR</dt>
        <dd class="stat__value numeric">{{ fmt(meanHr) }}</dd>
      </div>
      <div class="stat">
        <dt class="stat__label">Exp. RBI</dt>
        <dd class="stat__value numeric">{{ fmt(player.mean_rbi) }}</dd>
      </div>
    </dl>

    <!-- Hit distribution sparkline -->
    <section class="card__distribution" aria-label="Hit distribution">
      <div class="dist__header">
        <span class="dist__label">Hit Distribution</span>
        <span class="dist__hint numeric">per plate apps</span>
      </div>
      <svg
        viewBox="0 0 120 56"
        class="dist-svg"
        role="img"
        :aria-label="`Hit distribution for ${player.name}`"
      >
        <!-- Baseline ink rule -->
        <line x1="0" y1="42" x2="120" y2="42" stroke="#0D1B2F" stroke-width="0.6" />

        <g
          v-for="(val, idx) in player.hit_distribution.slice(0, 5)"
          :key="`hit-${idx}`"
          class="bar-group"
        >
          <title>{{ xLabels[idx] }}: {{ pct(val) }}</title>
          <rect :x="idx * 24 + 2" y="0" width="20" height="42" fill="transparent" />
          <rect
            :x="idx * 24 + 2"
            :y="42 - val * 40"
            width="20"
            :height="val * 40"
            fill="#0D1B2F"
            opacity="0.85"
            class="bar-rect"
          />
          <text
            :x="idx * 24 + 12"
            :y="Math.max(9, 42 - val * 40 - 3)"
            class="bar-pct numeric"
            text-anchor="middle"
          >
            {{ pct(val) }}
          </text>
          <text :x="idx * 24 + 12" y="53" class="bar-x-label numeric" text-anchor="middle">
            {{ xLabels[idx] }}
          </text>
        </g>
      </svg>
    </section>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Player {
  player_id: string | number
  name: string
  team: string
  position: string | null
  mean_hits: number
  mean_hr: number
  mean_rbi: number
  hit_distribution: number[]
  hr_distribution: number[]
}

const props = defineProps<{
  player: Player
  index?: number
}>()

const meanHits = computed((): number | undefined => props.player.mean_hits)
const meanHr = computed((): number | undefined => props.player.mean_hr)

const xLabels = ['0H', '1H', '2H', '3H', '4H+']

function pct(val: number): string {
  return `${(val * 100).toFixed(1)}%`
}

function fmt(val: number | undefined | null): string {
  if (val === undefined || val === null || Number.isNaN(val)) return '—'
  return val.toFixed(2)
}

const paddedIndex = computed((): string => {
  const i = (props.index ?? 0) + 1
  return String(i).padStart(2, '0')
})

const delay = Math.min((props.index ?? 0) * 0.06, 0.42)
</script>

<style scoped>
.card {
  background: var(--paper-deep);
  padding: 1.5rem 1.5rem 1.25rem;
  position: relative;
  animation: fadeUp 0.55s ease both;
  animation-delay: var(--delay);
  transition: transform 0.18s ease;
}

.card:hover {
  transform: translate(-2px, -2px);
}

.card__corner {
  position: absolute;
  top: 0.85rem;
  right: 1.1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.15rem;
}

.card__n {
  font-family: var(--font-data);
  font-size: 0.65rem;
  letter-spacing: 0.16em;
  color: var(--ink-ghost);
  text-transform: uppercase;
}

.card__position {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ox);
}

.card__header {
  padding-right: 3.5rem; /* leave space for corner chip */
  margin-bottom: 0.45rem;
}

.card__name {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 48,
    'wght' 700;
  font-size: 1.45rem;
  line-height: 1.05;
  color: var(--ink);
  letter-spacing: -0.015em;
  margin-bottom: 0.2rem;
}

.card__team {
  font-family: var(--font-data);
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  color: var(--ink-faint);
  text-transform: uppercase;
}

.card__rule {
  border: none;
  border-top: var(--rule-thin) solid var(--ink);
  margin: 0.85rem 0 1rem;
}

/* ─── Stats row ──────────────────────────────────────────────────── */
.card__stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 1.1rem;
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.stat__label {
  font-family: var(--font-ui);
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--ink-faint);
}

.stat__value {
  font-family: var(--font-data);
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--ink);
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

/* ─── Distribution ───────────────────────────────────────────────── */
.card__distribution {
  padding-top: 0.9rem;
  border-top: var(--rule-thin) dashed var(--paper-edge);
}

.dist__header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.5rem;
}

.dist__label {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--ox);
}

.dist__hint {
  font-size: 0.6rem;
  letter-spacing: 0.12em;
  color: var(--ink-ghost);
  text-transform: uppercase;
}

.dist-svg {
  width: 100%;
  height: auto;
  display: block;
}

.bar-group {
  cursor: default;
}

.bar-rect {
  transition:
    opacity 0.15s ease,
    fill 0.15s ease;
}

.bar-group:hover .bar-rect {
  opacity: 1;
  fill: #8b1a1a;
}

.bar-pct {
  font-family: 'JetBrains Mono', monospace;
  font-size: 6.5px;
  fill: #0d1b2f;
  opacity: 0;
  transition: opacity 0.15s;
  pointer-events: none;
}

.bar-group:hover .bar-pct {
  opacity: 1;
}

.bar-x-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 7px;
  fill: #6b6656;
  letter-spacing: 0.04em;
  pointer-events: none;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
